/*
  Seitlicher Warenkorb: Öffnen, Schließen, Nachladen, Konfetti.

  Der Inhalt wird serverseitig über die Section Rendering API geholt.
  Preise werden also von Shopify formatiert — im Browser nachgebaute
  Formate gehen bei Rabatten und fremden Währungen regelmäßig schief.

  Die Abschnitts-ID kommt aus data-section-id und wird NICHT hart
  hineingeschrieben: in einer Section-Group heißt der Abschnitt so, wie
  der Schlüssel in footer-group.json lautet — hier `cart_drawer` mit
  Unterstrich. Eine falsch geratene ID liefert eine Antwort ohne
  passenden Schlüssel, und der Warenkorb bliebe stumm auf dem alten Stand.
*/
(() => {
  const wurzel = () => document.querySelector('[data-warenkorb]');
  if (!wurzel()) return;

  const bewegungAus = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.body.dataset.motion !== 'an';

  let letzterAusloeser = null;

  /* ---------------------------------------------------------------- *
     Konfetti — eigener kleiner Renderer, keine Fremdbibliothek
   * ---------------------------------------------------------------- */
  function konfetti(flaeche) {
    if (!flaeche || bewegungAus()) return;

    const kontext = flaeche.getContext('2d');
    if (!kontext) return;

    const dichte = Math.min(window.devicePixelRatio || 1, 2);
    const breite = flaeche.clientWidth;
    const hoehe = flaeche.clientHeight;
    if (!breite || !hoehe) return;

    flaeche.width = breite * dichte;
    flaeche.height = hoehe * dichte;
    kontext.scale(dichte, dichte);

    const stil = getComputedStyle(document.documentElement);
    const farben = [
      '#D4AF37',
      '#E8D38A',
      '#F3E5AB',
      '#C5A059',
      '#FAF8F5'
    ];

    // Fällt von oben nach unten gerade herunter — kein Hängenbleiben, kein vorzeitiges Verschwinden
    const teilchen = Array.from({ length: 85 }, () => ({
      x: Math.random() * breite,
      y: -15 - Math.random() * 120,
      b: 6 + Math.random() * 5,
      h: 8 + Math.random() * 6,
      dy: 4.2 + Math.random() * 3.4,
      ay: 0.035,
      dreh: Math.random() * Math.PI,
      ddreh: (Math.random() - 0.5) * 0.16,
      farbe: farben[Math.floor(Math.random() * farben.length)]
    }));

    let animId = null;

    function bild() {
      kontext.clearRect(0, 0, breite, hoehe);
      let nochSichtbar = false;

      teilchen.forEach((t) => {
        // Gerader Sturzflug von oben nach unten ohne seitliches Trudeln
        t.y += t.dy;
        t.dy += t.ay;
        t.dreh += t.ddreh;

        if (t.y < hoehe + 30) {
          nochSichtbar = true;
        }

        kontext.save();
        kontext.translate(t.x, t.y);
        kontext.rotate(t.dreh);
        kontext.fillStyle = t.farbe;
        kontext.fillRect(-t.b / 2, -t.h / 2, t.b, t.h);
        kontext.restore();
      });

      // Läuft solange bis ALLE Partikel unten herausgefallen sind — verschwindet NIE mitten im Flug!
      if (nochSichtbar) {
        animId = window.requestAnimationFrame(bild);
      } else {
        kontext.clearRect(0, 0, breite, hoehe);
      }
    }

    if (animId) window.cancelAnimationFrame(animId);
    animId = window.requestAnimationFrame(bild);
  }

  /* ---------------------------------------------------------------- *
     Öffnen und Schließen
   * ---------------------------------------------------------------- */
  function fokusFangen(tafel, ereignis) {
    if (ereignis.key !== 'Tab') return;

    const ziele = tafel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!ziele.length) return;

    const erstes = ziele[0];
    const letztes = ziele[ziele.length - 1];

    if (ereignis.shiftKey && document.activeElement === erstes) {
      ereignis.preventDefault();
      letztes.focus();
    } else if (!ereignis.shiftKey && document.activeElement === letztes) {
      ereignis.preventDefault();
      erstes.focus();
    }
  }

  function oeffnen(mitKonfetti) {
    const w = wurzel();
    if (!w) return;

    const tafel = w.querySelector('[data-warenkorb-tafel]');
    const schleier = w.querySelector('[data-warenkorb-schliessen]');
    if (!tafel) return;

    schleier.hidden = false;
    tafel.hidden = false;
    document.body.style.overflow = 'hidden';

    // Reflow erzwingen, damit der Übergang von translateX(100%) aus läuft.
    // Bewusst kein requestAnimationFrame: das feuert in unsichtbaren
    // Tabs nicht, und dann bliebe der Warenkorb hängen.
    void tafel.offsetWidth;
    w.classList.add('ist-offen');
    tafel.querySelector('[data-warenkorb-schliessen]')?.focus({ preventScroll: true });

    if (mitKonfetti && w.dataset.konfetti === 'an' && w.dataset.anzahl !== '0') {
      window.setTimeout(() => konfetti(tafel.querySelector('[data-konfetti-flaeche]')), 260);
    }
  }

  function schliessen() {
    const w = wurzel();
    if (!w) return;

    const tafel = w.querySelector('[data-warenkorb-tafel]');
    const schleier = w.querySelector('.wk__schleier');
    if (!tafel || tafel.hidden) return;

    w.classList.remove('ist-offen');
    tafel.hidden = true;
    if (schleier) schleier.hidden = true;
    document.body.style.overflow = '';
    letzterAusloeser?.focus?.({ preventScroll: true });
  }

  /* ---------------------------------------------------------------- *
     Inhalt neu holen
   * ---------------------------------------------------------------- */
  async function neuLaden() {
    const kennung = wurzel()?.dataset.sectionId;
    if (!kennung) return;

    /*
      Bewusst OHNE Accept-Header: mit `Accept: application/json` antwortet
      Shopify auf Kollektions- und Produktpfaden mit der jeweiligen
      Ressourcen-API und ignoriert `?sections=` — dann kommt eine Antwort
      zurück, in der der Abschnitt gar nicht vorkommt.

      Und bewusst vom Wurzelpfad aus: der Abschnitt liegt in der
      Fußbereichs-Gruppe, sein Inhalt hängt an keinem Seitenpfad.
    */
    const wurzelPfad = window.Shopify?.routes?.root || '/';
    const antwort = await fetch(`${wurzelPfad}?sections=${encodeURIComponent(kennung)}`);
    if (!antwort.ok) return;

    const daten = await antwort.json();
    const html = daten[kennung];
    if (!html) return;

    const neu = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-warenkorb]');
    const alt = wurzel();
    if (!neu || !alt) return;

    const warOffen = !alt.querySelector('[data-warenkorb-tafel]')?.hidden;
    alt.replaceWith(neu);

    if (warOffen) {
      const tafel = neu.querySelector('[data-warenkorb-tafel]');
      const schleier = neu.querySelector('.wk__schleier');
      if (tafel) tafel.hidden = false;
      if (schleier) schleier.hidden = false;
      neu.classList.add('ist-offen');
    }
  }

  /* ---------------------------------------------------------------- *
     Ereignisse
   * ---------------------------------------------------------------- */
  document.addEventListener('click', (ereignis) => {
    const auf = ereignis.target.closest('[data-cart-trigger]');
    if (auf) {
      ereignis.preventDefault();
      letzterAusloeser = auf;
      oeffnen(true);
      return;
    }

    if (ereignis.target.closest('[data-warenkorb-schliessen]')) {
      ereignis.preventDefault();
      schliessen();
    }

    if (ereignis.target.closest('[data-warenkorb-schliessen-nav]')) {
      schliessen();
    }
  });

  document.addEventListener('keydown', (ereignis) => {
    const tafel = wurzel()?.querySelector('[data-warenkorb-tafel]');
    if (!tafel || tafel.hidden) return;

    if (ereignis.key === 'Escape') {
      schliessen();
      return;
    }

    fokusFangen(tafel, ereignis);
  });

  // Mengenknöpfe im Warenkorb
  document.addEventListener('click', (ereignis) => {
    const knopf = ereignis.target.closest('[data-cart-schritt]');
    if (!knopf) return;

    const feld = knopf.parentElement.querySelector('[data-cart-menge]');
    if (!feld) return;

    const schritt = parseInt(knopf.dataset.cartSchritt, 10);
    feld.value = Math.max(0, (parseInt(feld.value, 10) || 0) + schritt);
    feld.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------------------------------------------------------- *
     Auf Warenkorbänderungen hören — cart.js meldet sie
   * ---------------------------------------------------------------- */
  document.addEventListener('bbc:cart:added', () => {
    neuLaden().then(() => {
      // Beim Nachlegen kein Konfetti, sondern dedizierte Zeilen-Animation
      oeffnen(false);
      const erste = wurzel()?.querySelector('.wkz');
      if (erste) {
        erste.classList.remove('wkz--neu');
        void erste.offsetWidth;
        erste.classList.add('wkz--neu');
      }
    });
  });

  document.addEventListener('bbc:cart:changed', () => {
    neuLaden();
  });
})();
